import {
  Sparkles,
  MessageSquare,
  ListChecks,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import type { EvidenceScoreResult } from '@deepguide-ai/types'
import { QualityScoreDisplay, getQualityTierBadge } from './QualityScoreDisplay'

interface ElementScore {
  elementId: string
  elementName: string
  isValid: boolean
  reasoningText?: string | null
}

interface AIAssessmentSectionProps {
  /** Overall AI quality score (0-100) */
  overallScore: number
  scoreResult: EvidenceScoreResult
  /** Quality tier: EXCELLENT, ACCEPTABLE, NEEDS_IMPROVEMENT, FAIL */
  qualityTier?: string | null
  /** AI feedback text */
  feedback?: string | null
  /** Element-by-element scores */
  elementScores?: ElementScore[] | null
  /** Whether to show element scores (reviewers only) */
  showElementScores?: boolean
}

/**
 * Reusable AI Assessment section for displaying submission quality scores
 * Used in CurrentEvidenceCard and other places that show AI scoring
 */
export function AIAssessmentSection({
  overallScore,
  scoreResult,
  qualityTier,
  feedback,
  elementScores,
  showElementScores = false,
}: AIAssessmentSectionProps) {
  const hasElementScores = elementScores && elementScores.length > 0

  return (
    <div className="space-y-3">
      {/* Score display */}
      <div className='rounded-md border bg-muted/30 p-3'>
        <div className='flex items-center justify-between mb-2'>
          <div className='flex items-center gap-2'>
            <Sparkles className='h-4 w-4 text-muted-foreground' />
            <span className='text-sm font-medium'>AI Score</span>
          </div>
          {getQualityTierBadge(qualityTier)}
        </div>
        <QualityScoreDisplay
          score={overallScore}
          result={scoreResult}
          variant='card'
        />
        {/* Validated elements count */}
        {/* {showElementScores && hasElementScores && (
          <p className='text-xs text-muted-foreground mt-1.5'>
            {elementScores.filter(e => e.isValid).length}/{elementScores.length} elements validated
          </p>
        )} */}
      </div>
      {/* AI Feedback */}
      {feedback && (
        <div className='space-y-1.5'>
          <div className='flex items-center gap-1.5 text-xs font-medium text-muted-foreground'>
            <MessageSquare className='h-3.5 w-3.5' />
            AI Review
          </div>
          <p className='text-sm text-muted-foreground leading-relaxed'>
            {feedback}
          </p>
        </div>
      )}
      {/* Element Scores - reviewers only */}
      {showElementScores && hasElementScores && (
        <div className='space-y-1.5'>
          <div className='flex items-center gap-1.5 text-xs font-medium text-muted-foreground'>
            <ListChecks className='h-3.5 w-3.5' />
            Evidence Elements
          </div>
          <div className='space-y-1.5'>
            {elementScores.map((element) => (
              <div
                key={element.elementId}
                className='flex items-start gap-2 text-sm'
              >
                {element.isValid ? (
                  <CheckCircle className='h-4 w-4 text-status-green mt-0.5 shrink-0' />
                ) : (
                  <XCircle className='h-4 w-4 text-status-red mt-0.5 shrink-0' />
                )}
                <div className='flex-1 min-w-0'>
                  <span className={element.isValid ? 'text-foreground' : 'text-muted-foreground'}>
                    {element.elementName}
                  </span>
                  {!element.isValid && element.reasoningText && (
                    <p className='text-xs text-muted-foreground mt-0.5'>
                      {element.reasoningText}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
